import React, { useState, useRef } from 'react';
import ReactCrop, { centerCrop, makeAspectCrop } from 'react-image-crop';
import 'react-image-crop/dist/ReactCrop.css';
import { Box, Typography, Paper, CircularProgress, Grid } from '@mui/material';
import Button from './UIKit/Button';
import { useTranslation } from 'react-i18next';

/**
 * Центрирует кроп с заданными пропорциями
 */
function centerAspectCrop(mediaWidth, mediaHeight, aspect) {
  return centerCrop(
    makeAspectCrop(
      {
        unit: '%',
        width: 90,
      },
      aspect,
      mediaWidth,
      mediaHeight
    ),
    mediaWidth,
    mediaHeight
  );
}

/**
 * Компонент для кроппинга изображений
 */
const ImageCropper = ({ image, onCropComplete, onCancel, aspectRatio = 1 }) => {
  const { t } = useTranslation(['common']);
  const [crop, setCrop] = useState();
  const [completedCrop, setCompletedCrop] = useState(null);
  const [isProcessing, setIsProcessing] = useState(false);
  const imgRef = useRef(null);

  const onImageLoad = (e) => {
    const { width, height } = e.currentTarget;
    setCrop(centerAspectCrop(width, height, aspectRatio));
  };

  const createCroppedImage = async () => {
    if (!completedCrop || !imgRef.current) {
      return;
    }

    setIsProcessing(true);

    const image = imgRef.current;
    const canvas = document.createElement('canvas');
    const crop = completedCrop;

    const scaleX = image.naturalWidth / image.width;
    const scaleY = image.naturalHeight / image.height;

    canvas.width = crop.width * scaleX;
    canvas.height = crop.height * scaleY;

    const ctx = canvas.getContext('2d');

    ctx.drawImage(
      image,
      crop.x * scaleX,
      crop.y * scaleY,
      crop.width * scaleX,
      crop.height * scaleY,
      0,
      0,
      crop.width * scaleX,
      crop.height * scaleY
    );

    // Конвертируем canvas в blob
    canvas.toBlob((blob) => {
      if (!blob) {
        console.error(t('common:imageCropper.errors.blobCreation'));
        setIsProcessing(false);
        return;
      }

      // Создаем URL для blob
      const croppedImageUrl = URL.createObjectURL(blob);

      // Создаем File из blob для отправки на сервер
      const croppedImageFile = new File([blob], 'cropped-image.png', {
        type: 'image/png',
      });

      onCropComplete(croppedImageUrl, croppedImageFile);
      setIsProcessing(false);
    }, 'image/png');
  };

  return (
    <Paper elevation={0} sx={{ p: 2, mb: 2 }}>
      <Typography variant="h6" gutterBottom>
        {t('common:imageCropper.title')}
      </Typography>
      <Typography variant="body2" color="text.secondary" gutterBottom>
        {t('common:imageCropper.instruction')}
      </Typography>

      <Box sx={{ maxWidth: '100%', overflow: 'hidden', mt: 2, mb: 2 }}>
        <ReactCrop
          crop={crop}
          onChange={(_, percentCrop) => setCrop(percentCrop)}
          onComplete={(c) => setCompletedCrop(c)}
          aspect={aspectRatio}
          minWidth={50}
          minHeight={50}
        >
          <img
            ref={imgRef}
            src={image}
            alt={t('common:imageCropper.imageAlt')}
            style={{ maxWidth: '100%' }}
            onLoad={onImageLoad}
          />
        </ReactCrop>
      </Box>

      <Grid container spacing={2} justifyContent="flex-end">
        <Grid item>
          <Button onClick={onCancel} variant="outlined">
            {t('common:actions.cancel')}
          </Button>
        </Grid>
        <Grid item>
          <Button
            onClick={createCroppedImage}
            variant="contained"
            disabled={!completedCrop || isProcessing}
          >
            {isProcessing ? (
              <CircularProgress size={24} />
            ) : (
              t('common:actions.apply')
            )}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default ImageCropper;
